import { useMutation } from "@apollo/client";
import { useAbility } from "@casl/react";
import {
  Checkbox,
  Container,
  Form,
  InnerForm,
  Modal,
  OutlineContainer,
  RadioButton,
  Row,
  TextInput,
} from "@fluidattacks/design";
import { useCallback } from "react";
import { Fragment } from "react/jsx-runtime";
import { useTranslation } from "react-i18next";

import type {
  IEnvironmentConfigSubmit,
  IModalEditConfigurationProps,
} from "./types";

import { authzPermissionsContext } from "context/authz/config";
import { UPDATE_ENVIRONMENT_CONFIGURATION } from "pages/group/scope/queries";
import { msgError, msgSuccess } from "utils/notifications";

const EditConfigurationModal = (
  props: IModalEditConfigurationProps,
): JSX.Element | null => {
  const { modalRef, environment, groupName, rootId, onUpdate } = props;
  const urlId = environment.id;
  const { t } = useTranslation();
  const permissions = useAbility(authzPermissionsContext);
  const canEditEnvConfiguration = permissions.can(
    "integrates_api_mutations_update_git_root_environment_configuration_mutate",
  );
  const { close } = modalRef;
  // Graphql operations
  const [updateEnvironmentConfiguration] = useMutation(
    UPDATE_ENVIRONMENT_CONFIGURATION,
    {
      onCompleted: (): void => {
        msgSuccess(
          t("group.scope.git.editEnvironmentConfiguration.successUpdate"),
          t("group.scope.git.editEnvironmentConfiguration.successTitle"),
        );
        onUpdate?.();
        close();
      },
      onError: ({ graphQLErrors }): void => {
        graphQLErrors.forEach((error): void => {
          if (
            error.message ===
            "Exception - Access denied or root environment url not found"
          ) {
            msgError(t("validations.environmentUrlRemoved"));
          }

          msgError(error.message);
        });
      },
    },
  );
  const mapBoolean = (value: string): boolean => value === "true";
  const handleUpdateSubmit = useCallback(
    async ({
      isProduction,
      useEgress,
      useZtna,
    }: {
      isProduction: string;
      useEgress: boolean;
      useZtna: boolean;
    }): Promise<void> => {
      if (!canEditEnvConfiguration) {
        msgError(
          t(
            "group.scope.git.editEnvironmentConfiguration.permissions.editEnvConfiguration",
          ),
        );

        return;
      }
      await updateEnvironmentConfiguration({
        variables: {
          groupName,
          isProduction: mapBoolean(isProduction),
          rootId,
          urlId,
          useEgress,
          useZtna,
        },
      });
    },
    [
      updateEnvironmentConfiguration,
      rootId,
      groupName,
      urlId,
      t,
      canEditEnvConfiguration,
    ],
  );

  return (
    <Fragment>
      <Modal
        id={"edit-config-modal"}
        modalRef={modalRef}
        size={"sm"}
        title={t("group.scope.git.editEnvironmentConfiguration.title")}
      >
        <Form
          cancelButton={{ onClick: close }}
          confirmButton={{ disabled: !canEditEnvConfiguration }}
          defaultValues={{
            groupName,
            isProduction: environment.isProduction ? "true" : "false",
            rootId,
            url: environment.url,
            urlId: environment.id,
            useEgress: environment.useEgress,
            useZtna: environment.useZtna,
          }}
          id={"edit-env-configuration"}
          onSubmit={handleUpdateSubmit}
        >
          <InnerForm<IEnvironmentConfigSubmit>>
            {({ formState, register, watch }): JSX.Element => {
              const [isProduction, url, useEgress, useZtna] = watch([
                "isProduction",
                "url",
                "useEgress",
                "useZtna",
              ]);

              return (
                <Fragment>
                  <TextInput
                    defaultValue={url}
                    disabled={true}
                    label={t(
                      "group.scope.git.editEnvironmentConfiguration.text",
                    )}
                    name={"url"}
                    register={register}
                    tooltip={t(
                      "group.scope.git.editEnvironmentConfiguration.toolTip",
                    )}
                  />
                  <Row>
                    &nbsp;
                    <Checkbox
                      {...register("useZtna")}
                      disabled={Boolean(useEgress)}
                      label={t("group.scope.git.repo.useZtna.label")}
                      name={"useZtna"}
                      tooltip={t("group.scope.git.repo.useZtna.toolTip")}
                    />
                    <Checkbox
                      {...register("useEgress")}
                      disabled={Boolean(useZtna)}
                      label={t("group.scope.git.repo.useEgress.label")}
                      name={"useEgress"}
                      tooltip={t("group.scope.git.repo.useEgress.toolTip")}
                    />
                  </Row>

                  <Container mt={1}>
                    <OutlineContainer
                      error={formState.errors.isProduction?.message?.toString()}
                      htmlFor={"isProduction"}
                      label={t("group.scope.git.addEnvironment.isProduction")}
                      weight={"bold"}
                    >
                      <Container
                        alignSelf={"start"}
                        display={"flex"}
                        gap={1}
                        width={"fit-content"}
                      >
                        <RadioButton
                          {...register("isProduction")}
                          defaultChecked={isProduction === "true"}
                          label={t(
                            "group.scope.git.addEnvironment.yesIsProduction",
                          )}
                          name={"isProduction"}
                          value={"true"}
                        />
                        <RadioButton
                          {...register("isProduction")}
                          defaultChecked={isProduction === "false"}
                          label={t(
                            "group.scope.git.addEnvironment.noIsProduction",
                          )}
                          name={"isProduction"}
                          value={"false"}
                        />
                      </Container>
                    </OutlineContainer>
                  </Container>
                </Fragment>
              );
            }}
          </InnerForm>
        </Form>
      </Modal>
    </Fragment>
  );
};

export { EditConfigurationModal };
